<template>
  <div style="min-height: calc(100vh - 176px)" class="flex flex-wrap mt-4">
    <div class="relative py-2 px-2 flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-blueGray-100 border-0">
      <div class="container mx-auto px-4 py-4">
        <h1 class="text-2xl font-bold mb-4">
          {{ isEditing ? "Редактировать пользователя" : "Создать пользователя" }}
        </h1>

        <form @submit.prevent="saveUser" class="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2">ФИО</label>
            <input
              type="text"
              v-model="user.fio"
              placeholder="Введите ФИО"
              class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
              required
            />
          </div>

          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2">Логин</label>
            <input
              type="text"
              v-model="user.login"
              placeholder="Введите логин"
              class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
              required
            />
          </div>

          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2">Email (опционально)</label>
            <input
              type="email"
              v-model="user.email"
              placeholder="Введите Email"
              class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
            />
          </div>

          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2">Телефон</label>
            <input
              type="tel"
              v-model="user.phone"
              placeholder="+7xxxxxxxxxx"
              class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
            />
            <small class="text-gray-500">Формат: +7xxxxxxxxxx</small>
          </div>

          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2">Роль</label>
            <select
              v-model="user.role"
              class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
              required
            >
              <option disabled value="">Выберите роль</option>
              <option v-for="role in roles" :key="role.key" :value="role.key">
                {{ role.val }}
              </option>
            </select>
          </div>

          <div class="mb-4 flex items-center">
            <input type="checkbox" v-model="user.is_enabled" id="is_enabled" class="mr-2 leading-tight">
            <label for="is_enabled" class="text-gray-700 text-sm font-bold">Активен</label>
          </div>

          <!-- Блок пароля -->
          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2">Пароль</label>
            <div class="relative">
              <input
                :type="showPassword ? 'text' : 'password'"
                v-model="user.password"
                :required="!isEditing"
                placeholder="Введите пароль"
                class="shadow appearance-none border rounded w-full py-2 pl-3 pr-10 text-gray-700"
              />
              <PasswordVisibilityToggle
                :visible="showPassword"
                @toggle="showPassword = !showPassword"
              />
            </div>
          </div>

          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2">Подтвердите пароль</label>
            <div class="relative">
              <input
                :type="showPassword2 ? 'text' : 'password'"
                v-model="user.password2"
                :required="!isEditing"
                placeholder="Повторите пароль"
                class="shadow appearance-none border rounded w-full py-2 pl-3 pr-10 text-gray-700"
              />
              <PasswordVisibilityToggle
                :visible="showPassword2"
                show-label="Показать повтор пароля"
                hide-label="Скрыть повтор пароля"
                @toggle="showPassword2 = !showPassword2"
              />
            </div>
          </div>

          <div class="flex gap-2">
            <button
              type="submit"
              class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            >
              Сохранить
            </button>
            <button
              type="button"
              @click="$router.push('/admin/users')"
              class="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
            >
              Назад
            </button>
          </div>
        </form>

        <!-- Отображение ошибок -->
        <div v-if="errors && Object.keys(errors).length > 0" class="bg-red-50 border border-red-400 rounded px-4 py-3 mt-4">
          <h3 class="text-red-800 font-bold mb-2">Ошибки при сохранении:</h3>
          <ul class="text-red-700 text-sm">
            <li v-for="(message, field) in errors" :key="field" class="mb-1">
              <strong>{{ field }}:</strong> {{ Array.isArray(message) ? message.join(', ') : message }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Успешное сохранение -->
    <div
      v-if="showSuccessModal"
      class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50"
    >
      <div class="bg-white rounded-lg shadow-lg px-8 py-6 text-center">
        <div class="text-green-600 text-3xl mb-2">✔</div>
        <div class="text-lg font-semibold mb-2">Пользователь успешно сохранён!</div>
        <button
          @click="goBack"
          class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mt-4"
        >
          Вернуться к списку
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { API_USERS, HEADERS_TOKEN_JSON } from '@/const/uri';
import PasswordVisibilityToggle from "@/components/Forms/PasswordVisibilityToggle.vue";

export default {
  components: {
    PasswordVisibilityToggle,
  },
  data() {
    return {
      user: {
        fio: '',
        login: '',
        email: '',
        phone: '',
        role: '',
        is_enabled: true,
        password: '',
        password2: '',
      },
      roles: [
        { key: 0, val: 'Админ' },
        { key: 1, val: 'Ученик' },
        { key: 2, val: 'Эксперт' },
        { key: 3, val: 'Модератор' },
        { key: 4, val: 'Представитель компании' },
      ],
      isEditing: false,
      showSuccessModal: false,
      showPassword: false,
      showPassword2: false,
      errors: {},
    };
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      const id = this.$route.params.id;
      if (id) {
        this.isEditing = true;
        try {
          const response = await axios.get(`${API_USERS.list}${id}/`, {
            headers: HEADERS_TOKEN_JSON(),
          });
          if (response.data.code === 0) {
            const data = response.data.data;
            this.user.fio = data.fio || '';
            this.user.email = data.email || '';
            this.user.phone = data.phone || '';
            this.user.role = data.role?.key || '';
            this.user.login = data.login || '';
            this.user.is_enabled = data.is_enabled ?? true;
          }
        } catch (error) {
          console.error('Ошибка загрузки пользователя:', error);
        }
      }
    },
    async saveUser() {
      this.errors = {};

      if (this.user.password !== this.user.password2) {
        this.errors.password = 'Пароли не совпадают';
        return;
      }

      let payload = {
        fio: this.user.fio,
        login: this.user.login,
        email: this.user.email,
        phone: this.user.phone,
        role: this.user.role,
        is_enabled: this.user.is_enabled,
      };

      if (this.user.password) {
        payload.password = this.user.password;
        payload.password2 = this.user.password2;
      }

      try {
        if (this.isEditing) {
          await axios.patch(`${API_USERS.list}${this.$route.params.id}/`, payload, {
            headers: HEADERS_TOKEN_JSON(),
          });
        } else {
          await axios.post(API_USERS.list, payload, {
            headers: HEADERS_TOKEN_JSON(),
          });
        }
        this.showSuccessModal = true;
        setTimeout(() => {
          this.$router.push('/admin/users');
        }, 2000);
      } catch (error) {
        const errorData = error.response?.data;
        if (errorData && typeof errorData === 'object') {
          // Если ошибки в поле errors (структура API)
          if (errorData.errors && typeof errorData.errors === 'object') {
            // Преобразуем массивы в строки
            for (const [field, messages] of Object.entries(errorData.errors)) {
              this.errors[field] = Array.isArray(messages) ? messages.join(', ') : messages;
            }
          } else {
            this.errors = errorData;
          }
        } else {
          this.errors.general = 'Ошибка сохранения пользователя';
        }
        console.error('Ошибка сохранения пользователя:', error);
      }
    },
    goBack() {
      this.$router.push('/admin/users');
    }
  },
};
</script>
